/**
 * M7 S21: the finale commit and the one-way ladder (spec §8.1–§8.3, rev 3 D-332; T9 ladder half, T25, T26 unit
 * half, T27).
 *
 * `career.story.finale = { paths[], from? }` records every path committed, in order. **Blackmail** is the only path
 * that can be left, to Expose, Guardians or Sell; **Expose and Guardians** can each be added to the other and both
 * stay in force; **Sell is final**; **nothing returns to Blackmail**; Expose or Guardians never lead to Sell. The
 * allowed moves are `finale.json ladder`; everything else is a typed refusal that changes nothing (T27).
 *
 * Leaving Blackmail costs the same four things once, whichever path follows: the stipend stops (Blackmail is no
 * longer in force), every stipend credit paid comes back as a Tethys `clawback` debt over `blackmail.clawbackCycles`
 * cycles (the Mutual can refinance it, `leverage.ts`), the path's retaliation lands × `blackmailExitMult`, and the
 * clock surcharge runs `clock.json blackmailSurcharge.onlineDays` online days from `finale.exitOnlineDay` (S10 reads
 * it; a `surcharge` countdown re-sends the clock when it ends). Blackmail → Expose also tells the hearing about the
 * stipend (`stipend_known`, credibility −0.1); Guardians in force costs the sponsor −0.1; the case caps both at −0.2.
 *
 * Every commit: the path's writes (`paths.ts`), the `finaleCommit` or `ladderMove` echoes and callbacks (S11), a
 * `setWorldFlag` per flag it set, the clock (`setStoryClock`: the newest path's row, or the lower of Expose and
 * Guardians) and an autosave request (T24).
 *
 * Pure (Career in, Career out); event-driven, never per frame.
 */
import type { Career } from '../../career/types'
import { newlySetFlags, scheduleCallbacks } from '../callbacks'
import { pathsInForce, syncClock } from '../clock'
import { scheduleCountdown, type CountdownHandler } from '../countdowns'
import type { FinaleConfig } from '../schemas'
import { storyOf, withStory } from '../state'
import type { CountdownKind, FinalePath, StoryDebt, StoryFinale, StoryResult } from '../types'
import {
  blackmailWrites,
  exposeWrites,
  FINALE_COUNTDOWN,
  finaleData,
  guardiansWrites,
  sellWrites,
  type FinaleData,
  type FinaleInput,
  type PathMove,
} from './paths'

/** The tier the finale is offered at (spec §8 "Trigger: tier 5"). */
const FINALE_TIER = 5

/** Why a commit or a move is refused (T27). Each one leaves the career unchanged. */
export const FINALE_REFUSALS = [
  /** Before tier 5. */
  'notOffered',
  /** Sell is final. */
  'sellFinal',
  /** Nothing returns to Blackmail (the evidence is public or gone). */
  'noReturnToBlackmail',
  /** Expose or Guardians → Sell: the evidence is public, or the cell holds it. */
  'evidenceGone',
  /** The path is already in force. */
  'alreadyInForce',
  /** No `finale.json ladder` move leads there from a path in force. */
  'notOnLadder',
  /** Expose and Sell hand over a bundle: pick it first (`bundle.ts pickBundle`). */
  'noBundle',
] as const
export type FinaleRefusal = (typeof FINALE_REFUSALS)[number]

export type FinaleCommit =
  | { ok: true; result: StoryResult<Career>; move: PathMove }
  | { ok: false; reason: FinaleRefusal }

const NEEDS_BUNDLE: ReadonlySet<FinalePath> = new Set<FinalePath>(['expose', 'sell'])

/** The ladder move `to` would make from the paths in force: its `from`, or null when no move leads there. */
function ladderFrom(inForce: readonly FinalePath[], to: FinalePath, cfg: FinaleConfig): FinalePath | null {
  // Blackmail in force is the one being left; otherwise the newest path in force with a move to `to`.
  const order = inForce.includes('blackmail') ? ['blackmail' as const] : [...inForce].reverse()
  for (const from of order) if (cfg.ladder.some((m) => m.from === from && m.to === to)) return from
  return null
}

/** Why committing to `to` is refused now, or null when it is allowed. */
export function finaleRefusal(career: Career, to: FinalePath, cfg: FinaleConfig = finaleData().finale): FinaleRefusal | null {
  const story = storyOf(career)
  const inForce = pathsInForce(story.finale)
  if (story.finale.paths.length === 0) {
    if ((career.storyTier ?? 0) < FINALE_TIER) return 'notOffered'
  } else {
    if (inForce.includes('sell')) return 'sellFinal'
    if (to === 'blackmail') return 'noReturnToBlackmail'
    if (to === 'sell' && (inForce.includes('expose') || inForce.includes('guardians'))) return 'evidenceGone'
    if (inForce.includes(to)) return 'alreadyInForce'
    if (ladderFrom(inForce, to, cfg) === null) return 'notOnLadder'
  }
  if (NEEDS_BUNDLE.has(to) && !story.bundles.some((b) => b.path === to)) return 'noBundle'
  return null
}

/** The paths the player can commit to now (the finale offer and the ladder's options), in `FINALE_PATHS` order. */
export function finaleOptions(career: Career, cfg: FinaleConfig = finaleData().finale): FinalePath[] {
  const out: FinalePath[] = []
  for (const p of ['expose', 'blackmail', 'guardians', 'sell'] as const) {
    const r = finaleRefusal(career, p, cfg)
    if (r === null || r === 'noBundle') out.push(p)
  }
  return out
}

/** The common Blackmail exit (spec §8.3): the stipend stops, the clawback debt, the surcharge's start. */
function leaveBlackmail(career: Career, data: FinaleData): Career {
  const story = storyOf(career)
  const onlineDay = story.onlineDays
  const paid = story.finale.stipend?.paid ?? 0
  let next = withStory(career, (s) => {
    const debts: StoryDebt[] = [...(s.debts ?? [])]
    if (paid > 0 && !debts.some((d) => d.kind === 'clawback')) {
      debts.push({ kind: 'clawback', creditor: 'tethys', amount: paid, cyclesLeft: data.finale.blackmail.clawbackCycles })
    }
    const finale: StoryFinale = { ...s.finale, from: 'blackmail', exitOnlineDay: onlineDay }
    return { ...s, finale, debts }
  })
  next = scheduleCountdown(next, {
    id: FINALE_COUNTDOWN.surcharge,
    kind: 'surcharge',
    inOnlineDays: data.clock.blackmailSurcharge.onlineDays,
  })
  return next
}

function pathWrites(career: Career, move: PathMove, input: FinaleInput, data: FinaleData): StoryResult<Career> {
  switch (move.to) {
    case 'expose':
      return exposeWrites(career, move, input, data)
    case 'blackmail':
      return blackmailWrites(career, input, data)
    case 'guardians':
      return guardiansWrites(career, move, input, data)
    case 'sell':
      return sellWrites(career, move, input, data)
  }
}

/**
 * Commits `to`: the first finale commit, or a ladder move (spec §8.3). Refused (typed, nothing changes) when the
 * ladder doesn't allow it. The UI runs the roll call, the bundle pick and the confirmation first (S25, S39).
 */
export function commitFinale(career: Career, to: FinalePath, input: FinaleInput, data: FinaleData = finaleData()): FinaleCommit {
  const reason = finaleRefusal(career, to, data.finale)
  if (reason !== null) return { ok: false, reason }

  const before = career
  const inForce = pathsInForce(storyOf(career).finale)
  const first = storyOf(career).finale.paths.length === 0
  const from = first ? undefined : (ladderFrom(inForce, to, data.finale) ?? undefined)
  const blackmailExit = from === 'blackmail'
  const move: PathMove = from === undefined ? { to, blackmailExit } : { to, from, blackmailExit }

  // Record the move first: the case reads the paths for its credibility penalties.
  let next = blackmailExit ? leaveBlackmail(career, data) : career
  next = withStory(next, (s) => ({ ...s, finale: { ...s.finale, paths: [...s.finale.paths, to] } }))
  if (blackmailExit && to === 'expose') next = withStory(next, (s) => ({ ...s, flags: { ...s.flags, stipend_known: true } }))

  const acted = pathWrites(next, move, input, data)
  next = acted.career
  next = first
    ? scheduleCallbacks(next, 'finaleCommit', { vars: { to } })
    : scheduleCallbacks(next, 'ladderMove', { key: `${from ?? 'none'}-${to}`, vars: from === undefined ? { to } : { from, to } })

  const commands = [...acted.commands]
  for (const flag of newlySetFlags(before, next)) commands.push({ type: 'setWorldFlag', key: flag, value: true })
  const reyes = storyOf(next).reyesState
  if (reyes !== undefined && reyes !== storyOf(before).reyesState) commands.push({ type: 'setWorldFlag', key: 'reyes_state', value: reyes })
  const clock = syncClock(next, data.clock)
  commands.push(...clock.commands)
  const effects = [...acted.effects, { type: 'autosave' as const, reason: first ? `finale:${to}` : `finale:${from ?? 'none'}-${to}` }]
  return { ok: true, result: { career: clock.career, commands, effects }, move }
}

/**
 * Countdown handlers for `tickOnlineDay` (the story link merges them with the others): when the Blackmail surcharge
 * ends, re-derive the clock. (The Guardians floor's 1.3 → 1.8 step is read from `finale.floorUntilOnlineDay` by the
 * bridge's per-online-day `syncClock`; its `floor` countdown only carries the worker's floor.)
 */
export function finaleHandlers(data: FinaleData = finaleData()): Partial<Record<CountdownKind, CountdownHandler>> {
  const resync: CountdownHandler = (career, fired) =>
    fired.id === FINALE_COUNTDOWN.surcharge ? syncClock(career, data.clock) : { career, commands: [], effects: [] }
  return { surcharge: resync }
}
